import React, { lazy } from 'react'

export const CHUNK_RELOAD_KEY = 'chunk_reload_attempted'

export function isChunkError(error: Error): boolean {
    return (
        error.name === 'ChunkLoadError' ||
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Importing a module script failed')
    )
}

// Retries a dynamic import once before reloading. Exported for unit testing.
// Generic bound must accept components with any specific props shape; a narrower bound
// like ComponentType<Record<string, unknown>> rejects components with required typed props
// (verified via `tsc -b`, which a bare `tsc --noEmit` on this project does not run).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function importWithRetry<T extends React.ComponentType<any>>(
    importFn: () => Promise<{ default: T }>
): Promise<{ default: T }> {
    return importFn().catch(() =>
        importFn().catch(() => {
            // Both attempts failed — force a reload to get a fresh chunk manifest.
            // Guard with sessionStorage to prevent infinite reload loops.
            if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
                sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
                window.location.reload()
            }
            return { default: (() => null) as unknown as T }
        })
    )
}

// Wraps a lazy import with one automatic retry before giving up.
// This handles the common case where a new deploy changes chunk hashes — the
// first import attempt may hit the browser cache with a stale URL; the retry
// forces the network to fetch the updated chunk.
//
// If both attempts fail, falls back to a page reload (guarded by sessionStorage
// to prevent infinite reload loops when the chunk is genuinely broken).
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see importWithRetry above
export function lazyWithRetry<T extends React.ComponentType<any>>(
    importFn: () => Promise<{ default: T }>
) {
    return lazy(() => importWithRetry(importFn))
}
